import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import { Button, Grid, CircularProgress, FormControlLabel, Switch } from '@mui/material';
import { Input } from '@fullscreendigital/carrefour-dex-fe';
import { Modal } from 'components/core-ui/Modal';
import { useNotification } from 'hooks';
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete';
import { useCreateJobMutation, useJobCategoriesQuery, useJobDetailsQuery, useJobQuery, useUpdateJobMutation } from '../../api';
import s from 'components/core-ui/Modal/Modal.modules.scss';
import { JobCategory, JobTableItem, EditJobModalProps, JobDetailsItem } from '../../interfaces';
import { jobValidationSchema } from '../helpers';
import { isEmpty } from 'lodash';

const EditJobModal: React.FC<EditJobModalProps> = ({ isModalOpen = false, onModalClose, jobId, onSuccess }) => {
    const { showNotification } = useNotification();
    const shouldRefreshData = useMemo(() => isModalOpen === true, [isModalOpen]);

    const { data: job, isLoading } = useJobQuery(showNotification, jobId, shouldRefreshData);
    const { data: jobCategoriesData } = useJobCategoriesQuery(showNotification, '/iam/management-tools/job-categories');
    const { data: jobDetailsData } = useJobDetailsQuery(showNotification, '/iam/management-tools/job-details');

    const updateJobMutation = useUpdateJobMutation(showNotification);
    const createJobMutation = useCreateJobMutation(showNotification);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            jobTitle: job?.jobTitle || '',
            jobDetails: job?.jobDetails?.id,
            jobCategory: job?.jobCategory?.id,
            active: jobId ? job?.active : true
        },
        validationSchema: jobValidationSchema,
        onSubmit: async (values) => {
            const updatedValues: Partial<JobTableItem> = {
                ...values,
                jobCategory: { id: values.jobCategory },
                jobDetails: { id: values.jobDetails, active: values.active },
            };
                if (!jobId) {
                    await createJobMutation.mutateAsync(updatedValues);
                } else {
                    updatedValues.id = jobId;
                    await updateJobMutation.mutateAsync({ jobId, updatedData: updatedValues });
                }
                onSuccess();
                onModalClose();
                formik.resetForm();
        },
    });

    const getSelectedCategory = (categories: JobCategory[], categoryId: number) => {
        return categories?.find((cat) => cat.id === categoryId);
    };

    const getCategoryOptions = (categories: JobCategory[]) => {
        return categories?.map((cat) => ({ label: cat.name, value: cat.id }));
    };

    const selectedCategory = useMemo(() => getSelectedCategory(jobCategoriesData?.list, formik.values.jobCategory), [jobCategoriesData, formik.values.jobCategory]);
    const jobCategoryValue = useMemo(() => {
        return selectedCategory ? { label: selectedCategory.name ?? '', value: formik.values.jobCategory } : null;
    }, [selectedCategory, formik.values.jobCategory]);
    const jobCategoryOptions = useMemo(() => getCategoryOptions(jobCategoriesData?.list), [jobCategoriesData]);

    const getSelectedJobDetail = (jobDetails: JobDetailsItem[], jobDetailId: number) => {
        return jobDetails?.find((jd) => jd.id === jobDetailId);
    };

    const getJobDetailsOptions = (jobDetails: JobDetailsItem[]) => {
        return jobDetails?.map((jd) => ({ label: jd.name, value: jd.id }));
    };

    const selectedJobDetails = useMemo(() => getSelectedJobDetail(jobDetailsData?.list, formik.values.jobDetails), [jobDetailsData, formik.values.jobDetails]);
    const jobDetailsValue = useMemo(() => {
        return selectedJobDetails ? { label: selectedJobDetails.name ?? '', value: formik.values.jobDetails } : null;
    }, [selectedJobDetails, formik.values.jobDetails]);
    const jobDetailsOptions = useMemo(() => getJobDetailsOptions(jobDetailsData?.list), [jobDetailsData]);

    return (
        <Modal isModalOpen={isModalOpen} closeModal={onModalClose} modalTitle={jobId ? "Edit Job" : "Create Job"} modalWidth="50%">
            <Grid container spacing={2}>
                {isLoading || createJobMutation.isLoading || updateJobMutation.isLoading ? (
                    <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
                ) : (
                    <>
                        <Grid item xs={12}>
                            <Input
                                type="text"
                                variant="outlined"
                                name="jobTitle"
                                label="Job Title"
                                width="100%"
                                disabled={!!jobId}
                                value={formik.values.jobTitle}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
                                helperText={formik.touched.jobTitle && formik.errors.jobTitle}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AsyncAutocomplete
                                disableClearable
                                variant="outlined"
                                name="jobDetails"
                                label="Employee Category"
                                disabled={!isEmpty(job?.jobCategory)}
                                value={jobDetailsValue}
                                options={jobDetailsOptions}
                                onChange={(val) => formik.setFieldValue('jobDetails', val?.value)}
                                onBlur={() => formik.handleBlur('jobDetails')}
                                error={formik.touched.jobDetails && Boolean(formik.errors.jobDetails)}
                                helperText={formik.touched.jobDetails && formik.errors.jobDetails}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AsyncAutocomplete
                                disableClearable
                                variant="outlined"
                                name="jobCategory"
                                label="Job Category"
                                value={jobCategoryValue}
                                options={jobCategoryOptions}
                                onChange={(val) => formik.setFieldValue('jobCategory', val?.value)}
                                onBlur={() => formik.handleBlur('jobCategory')}
                                error={formik.touched.jobCategory && Boolean(formik.errors.jobCategory)}
                                helperText={formik.touched.jobCategory && formik.errors.jobCategory}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={
                                <Switch
                                    name="active"
                                    checked={formik.values.active}
                                    onChange={(event) => formik.setFieldValue('active', event.target.checked)}
                                    onBlur={formik.handleBlur}

                                />}
                                label="Active"
                            />
                        </Grid>
                        <Grid item xs={12} className={s.modalButtons}>
                            <Button variant="outlined" onClick={onModalClose}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => formik.handleSubmit()}
                                disabled={!formik.isValid || formik.isSubmitting}
                            >
                                Save
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>
        </Modal>
    );
};

export default EditJobModal;
